/**
 * Pantalla 6 (docs/FASE-1.md §6): `GET /api/v1/audit` (`apps/idp/src/platform/audit-log.ts`),
 * del evento más reciente al más antiguo.
 *
 * El estado `unavailable` se conserva a propósito: la consola puede desplegarse contra un núcleo
 * más antiguo que todavía no exponga la ruta, y en ese caso debe avisar en vez de romperse.
 */
import { useEffect, useState, type ReactElement } from "react";

import { auditApi, ConsoleApiError, type AuditLogEntry } from "../api/client";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { hasErrorCode, toDisplayMessage } from "../lib/errors";
import { formatDateTime } from "../lib/format";

type LoadState = "loading" | "unavailable" | "error" | "ready";

export function Audit(): ReactElement {
  const [state, setState] = useState<LoadState>("loading");
  const [items, setItems] = useState<AuditLogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    auditApi
      .list()
      .then((page) => {
        if (cancelled) return;
        setItems(page.items);
        setState("ready");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const notFound = err instanceof ConsoleApiError && (err.status === 404 || hasErrorCode(err, "not_found"));
        if (notFound) {
          setState("unavailable");
        } else {
          setError(toDisplayMessage(err));
          setState("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const columns: DataTableColumn<AuditLogEntry>[] = [
    { key: "createdAt", header: "Fecha", render: (e) => formatDateTime(e.createdAt) },
    { key: "actor", header: "Actor", render: (e) => `${e.actorType} · ${e.actorId}` },
    { key: "action", header: "Acción", render: (e) => e.action },
    { key: "target", header: "Objetivo", render: (e) => `${e.targetType} · ${e.targetId}` },
    { key: "org", header: "Organización", render: (e) => e.orgId ?? "—" },
  ];

  return (
    <section>
      <div className="page-header">
        <h1>Auditoría</h1>
      </div>
      {state === "loading" && <p>Cargando…</p>}
      {state === "unavailable" && (
        <p className="notice" role="status">
          No disponible en esta versión del API.
        </p>
      )}
      {state === "error" && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {state === "ready" && <DataTable columns={columns} rows={items} rowKey={(e) => e.id} emptyMessage="No hay eventos de auditoría." />}
    </section>
  );
}
